import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listFaq, createFaq, updateFaq, deleteFaq } from '../api/admin.js';
import type { FaqEntry } from '@m8-app/contracts/rest/faq.js';

const emptyForm = { questionEn: '', answerEn: '', questionHi: '', answerHi: '', sortOrder: 0, active: true };

export function Faq() {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['faq'],
    queryFn: listFaq,
  });

  const createMutation = useMutation({
    mutationFn: () => createFaq(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['faq'] });
      setShowAdd(false);
      setForm(emptyForm);
      setErr('');
    },
    onError: (e: unknown) => setErr((e as { message?: string }).message ?? 'Failed'),
  });

  const updateMutation = useMutation({
    mutationFn: () => updateFaq(editingId!, form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['faq'] });
      setEditingId(null);
      setErr('');
    },
    onError: (e: unknown) => setErr((e as { message?: string }).message ?? 'Failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteFaq(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['faq'] }),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (entry: FaqEntry) => updateFaq(entry.id, { active: !entry.active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['faq'] }),
  });

  function startEdit(entry: FaqEntry) {
    setEditingId(entry.id);
    setShowAdd(false);
    setForm({
      questionEn: entry.questionEn,
      answerEn: entry.answerEn,
      questionHi: entry.questionHi,
      answerHi: entry.answerHi,
      sortOrder: entry.sortOrder,
      active: entry.active,
    });
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (editingId) updateMutation.mutate();
    else createMutation.mutate();
  }

  const entries = data?.entries ?? [];

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">FAQ Content</h2>
          <p className="text-sm text-gray-500">
            Editable Q/A shown in the app's FAQ screen (HRD-8) — bilingual, no app release needed to change.
          </p>
        </div>
        <button
          onClick={() => { setShowAdd(true); setEditingId(null); setForm(emptyForm); setErr(''); }}
          className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700"
        >
          + New Entry
        </button>
      </div>

      {(showAdd || editingId) && (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6 max-w-3xl">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">{editingId ? 'Edit Entry' : 'New Entry'}</h3>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Question (English)</label>
              <input
                value={form.questionEn}
                onChange={(e) => setForm({ ...form, questionEn: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Question (Hindi)</label>
              <input
                value={form.questionHi}
                onChange={(e) => setForm({ ...form, questionHi: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mb-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Answer (English)</label>
              <textarea
                value={form.answerEn}
                onChange={(e) => setForm({ ...form, answerEn: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                rows={3}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Answer (Hindi)</label>
              <textarea
                value={form.answerHi}
                onChange={(e) => setForm({ ...form, answerHi: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
                rows={3}
                required
              />
            </div>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Sort order</label>
              <input
                type="number"
                value={form.sortOrder}
                onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value, 10) || 0 })}
                className="w-24 border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 mt-5">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({ ...form, active: e.target.checked })}
              />
              Active (visible in app)
            </label>
          </div>
          {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
          <div className="flex gap-3">
            <button type="submit" className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700">
              {editingId ? 'Save' : 'Create'}
            </button>
            <button
              type="button"
              onClick={() => { setShowAdd(false); setEditingId(null); setErr(''); }}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <p className="px-4 py-4 text-sm text-gray-400">Loading…</p>
        ) : isError ? (
          <p className="px-4 py-4 text-sm text-red-600">Couldn't load FAQ entries. Check your connection and try again.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-left">
              <tr>
                <th className="px-4 py-3 font-medium">Order</th>
                <th className="px-4 py-3 font-medium">Question (EN)</th>
                <th className="px-4 py-3 font-medium">Question (HI)</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-gray-400">{entry.sortOrder}</td>
                  <td className="px-4 py-3 max-w-xs truncate">{entry.questionEn}</td>
                  <td className="px-4 py-3 max-w-xs truncate">{entry.questionHi}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleActiveMutation.mutate(entry)}
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        entry.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}
                    >
                      {entry.active ? 'Active' : 'Hidden'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right space-x-3">
                    <button onClick={() => startEdit(entry)} className="text-xs text-indigo-600 hover:underline">Edit</button>
                    <button
                      onClick={() => { if (confirm('Delete this FAQ entry?')) deleteMutation.mutate(entry.id); }}
                      className="text-xs text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {entries.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-400">No FAQ entries</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
